import {
  SCANNER_COLUMN_CATALOG,
  SCANNER_COLUMN_MAP,
} from '@/features/scanner/schemas/scanner-column-defs'
import { buildColumnTree } from '@/features/sliced-form/utils/build-column-tree'
import type { ColumnCatalogDef, GroupNode } from '@/types/column-catalog'

/**
 * Curated subset of the scanner catalog, surfaced in the News column picker.
 * See:
 *   docs/superpowers/specs/2026-04-29-bar-chart-and-news-columns-design.md
 *   docs/superpowers/specs/2026-08-20-news-panel-behaviors-design.md (R7)
 *
 * Sections are hand-authored for the flat picker (2026-08-20): the scanner
 * defs still carry legacy category strings ("Day 1 Intraday" etc.), so each
 * entry's `category` is overridden with the section title below and `group`
 * is stripped so neither the flat list nor the filter tree nests further.
 * The module fails fast at load time if any key drifts out of the scanner
 * catalog (e.g. renamed upstream).
 */
const NEWS_SECTIONS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['Company', ['ticker', 'sector', 'industry', 'exchange', 'country', 'share_group']],
  [
    'Commonly Used',
    [
      'session_lp',
      'session_chg',
      'session_chg_p',
      'session_v',
      'dollar_vol',
      'rvol',
      'pm_chg_p',
      // PM high change %: the Premarket Gappers preset filters on it, and 1,698 migrated news
      // panels carry that leaf; without it the sheet shows "Select column…".
      'chg_pm_h_pc_pct',
      'pm_v',
    ],
  ],
  ['Price & Performance', ['o_chg_p', 'ah_chg_p', 'day_m1_close', 'pc_ytd_chg_p', 'halt', 'ssr']],
  ['Share Structure', ['marketcap', 'sharesfloat', 'sharesoutstanding', 'shortpercentfloat']],
]

export const NEWS_COLUMN_KEYS: readonly string[] = NEWS_SECTIONS.flatMap(([, keys]) => keys)

const missing = NEWS_COLUMN_KEYS.filter((key) => !SCANNER_COLUMN_MAP[key])
if (missing.length > 0) {
  throw new Error(`[news-column-defs] keys missing from scanner catalog: ${missing.join(', ')}`)
}

export const NEWS_COLUMN_CATALOG: ColumnCatalogDef[] = NEWS_SECTIONS.flatMap(([section, keys]) =>
  keys.map((key): ColumnCatalogDef => {
    const { group: _group, ...def } = SCANNER_COLUMN_MAP[key]
    return { ...def, category: section }
  }),
)

export const NEWS_COLUMN_MAP: Record<string, ColumnCatalogDef> = Object.fromEntries(
  NEWS_COLUMN_CATALOG.map((c) => [c.key, c]),
)

export const NEWS_COLUMNS_BY_CATEGORY: Record<string, ColumnCatalogDef[]> =
  NEWS_COLUMN_CATALOG.reduce<Record<string, ColumnCatalogDef[]>>((acc, c) => {
    ;(acc[c.category] ??= []).push(c)
    return acc
  }, {})

const built = buildColumnTree(NEWS_COLUMN_CATALOG)
export const NEWS_TREE: Record<string, GroupNode> = built.tree

// Anchor the scanner catalog import so module dependency is explicit.
void SCANNER_COLUMN_CATALOG

/** Columns a news panel shows when it has no persisted `selectedColumns`. */
export const DEFAULT_NEWS_COLUMNS = ['ticker']
